import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  GraduationCap, Plus, Save, Trash2, Edit3, Zap, Link2, X, CheckCircle2,
} from "lucide-react";

const EMPTY = { title: "", instruction: "", active: true };

// Volt-trenings-side. Hovedadmin kan skrive fri-tekst "skills" som Volt
// alltid tar hensyn til (høyeste prioritet). Kan åpnes med forhånds-fylt
// tekst via URL-parametre fra chat-log-siden ("Marker som feil"-flyt).
export default function VoltTrainingPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | item | "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat/training");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  // Når siden åpnes med query-parametre fra chat-log ("Marker som feil")
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const prefillInstr = params.get("instruction");
    const prefillTitle = params.get("title");
    const srcMsg = params.get("source_message_id");
    const srcSess = params.get("source_session_id");
    if (prefillInstr || prefillTitle) {
      setEditing("new");
      setForm({
        title: prefillTitle || "Korreksjon fra chat-logg",
        instruction: prefillInstr || "",
        active: true,
        source_message_id: srcMsg,
        source_session_id: srcSess,
      });
    }
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ title: it.title, instruction: it.instruction, active: it.active });
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.title.trim() || !form.instruction.trim()) {
      toast.error("Både tittel og instruksjon er påkrevd");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/chat/training", form);
        toast.success("Volt har fått en ny lærdom");
      } else {
        await api.put(`/chat/training/${editing.id}`, form);
        toast.success("Lærdom oppdatert");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (it) => {
    try {
      await api.put(`/chat/training/${it.id}`, { ...it, active: !it.active });
      load();
    } catch {
      toast.error("Kunne ikke endre");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Slette lærdommen "${it.title}"?`)) return;
    await api.delete(`/chat/training/${it.id}`);
    load();
  };

  const activeCount = items.filter((i) => i.active).length;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-4 lg:p-8 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#0F1A2B] flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-[#A57F39]" /> Volt-trening
            </h1>
            <p className="text-sm text-[#2B3F4D]/70 mt-1 max-w-2xl">
              Fri-tekst lærdommer Volt alltid følger. Skriv ting som "alltid gjør X",
              "aldri si Y" eller "rett opp Z". Aktive lærdommer har høyeste prioritet
              og overstyrer vanlig kunnskap ved konflikt.
            </p>
            <div className="text-xs text-[#A57F39] font-semibold mt-1">
              {activeCount} aktive · {items.length - activeCount} inaktive
            </div>
          </div>
          <Button
            onClick={openNew}
            data-testid="training-new"
            className="bg-[#A57F39] hover:bg-[#BD984D] text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Ny lærdom
          </Button>
        </div>

        {editing && (
          <Card className="border-[#A57F39] shadow-lg">
            <CardHeader className="flex-row items-center justify-between pb-2">
              <CardTitle className="text-base text-[#0F1A2B] flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#A57F39]" />
                {editing === "new" ? "Ny lærdom" : "Rediger lærdom"}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={close} data-testid="training-close">
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">
                  Kort tittel *
                </label>
                <Input
                  data-testid="training-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="F.eks. Riktig pris Greenline 39 høysesong"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">
                  Instruksjon til Volt *
                </label>
                <Textarea
                  data-testid="training-instruction"
                  value={form.instruction}
                  onChange={(e) => setForm((f) => ({ ...f, instruction: e.target.value }))}
                  rows={5}
                  placeholder='F.eks. "Prisen for Greenline 39 i høysesong 2027 er ca. 800 EUR per døgn – ikke 750 som tidligere oppgitt. Si alltid at prisen er indikativ."'
                />
                <p className="text-[11px] text-[#2B3F4D]/70 mt-1">
                  Volt leser denne som en instruks med høyeste prioritet. Vær tydelig – start gjerne med "Alltid …", "Aldri …" eller "Rett opp …".
                </p>
              </div>
              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 text-sm text-[#0F1A2B]">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                    data-testid="training-active"
                  />
                  Aktiv
                </label>
                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid="training-save"
                  className="bg-[#A57F39] hover:bg-[#BD984D] text-white"
                >
                  <Save className="h-4 w-4 mr-2" /> {saving ? "Lagrer…" : "Lagre"}
                </Button>
              </div>
              {form.source_message_id && (
                <div className="rounded-lg bg-[#A57F39]/5 border border-[#A57F39]/20 text-xs text-[#0F1A2B]/80 px-3 py-2 flex items-center gap-2">
                  <Link2 className="h-3.5 w-3.5" />
                  Koblet til Volt-svar <code className="bg-white/70 px-1 rounded">{form.source_message_id.slice(0, 8)}</code>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {loading && <div className="text-sm text-[#2B3F4D]/60">Laster …</div>}
          {!loading && items.length === 0 && (
            <Card className="border-dashed border-[#A57F39]/30">
              <CardContent className="py-10 text-center text-[#2B3F4D]/60">
                Ingen lærdommer enda. Klikk "Ny lærdom" for å lære Volt noe nytt.
              </CardContent>
            </Card>
          )}
          {items.map((it) => (
            <Card
              key={it.id}
              className={`border ${it.active ? "border-[#A57F39]/40" : "border-[#A57F39]/15 opacity-60"}`}
              data-testid={`training-row-${it.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#0F1A2B]">{it.title}</h3>
                      {it.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-widest">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-[10px] uppercase tracking-widest">
                          Inaktiv
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#2B3F4D] whitespace-pre-wrap">{it.instruction}</p>
                    <div className="text-[11px] text-[#2B3F4D]/60 mt-2 flex flex-wrap gap-3">
                      <span>Lagt til: {new Date(it.created_at).toLocaleDateString("no-NO")}</span>
                      {it.updated_at && it.updated_at !== it.created_at && (
                        <span>Sist endret: {new Date(it.updated_at).toLocaleDateString("no-NO")}</span>
                      )}
                      {it.created_by_name && <span>Av: {it.created_by_name}</span>}
                      {it.source_message_id && (
                        <span className="text-[#A57F39] flex items-center gap-1">
                          <Link2 className="h-3 w-3" /> Fra chat-logg
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggle(it)}
                      data-testid={`training-toggle-${it.id}`}
                    >
                      {it.active ? "Deaktiver" : "Aktiver"}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEdit(it)}
                      data-testid={`training-edit-${it.id}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(it)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`training-delete-${it.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
